import {
  ActorRefFrom,
  assertEvent,
  assign,
  enqueueActions,
  fromCallback,
  fromPromise,
  setup,
} from 'xstate';

import { formatErrorForLogging } from '@eluve/eluve-errors';
import { type LogAction, logAction } from '@eluve/frontend-machine-utils';
import {
  isAudioFileChunkFile,
  recordingTimestampFromFileName,
} from '@eluve/utils';

import { AppointmentFileIdbRow } from './files-db';
import { getSupportedAudioFormat } from './getSupportedAudioFormat';
import { lockManagerActor } from './periodicFileSync/actors/lockManager.actor';
import {
  CallbackActorInvoke,
  Events,
  SupportedAudioFormat,
  UploadFileEvents,
  UploadType,
} from './types';
import { UploadFileMachineInput } from './uploadFile.machine';

const SYNC_INTERVAL_MS_DEFAULT = 20 * 1000; // 20 second

export type GetFileFn = () => Promise<AppointmentFileIdbRow | null | undefined>;

type Input = {
  syncIntervalMs?: number;
  tenantId: string;
  userId: string;
  isOnline: boolean;
};

type Context = {
  tenantId: string;
  userId: string;
  syncIntervalMs: number;
  iterationCount: number;
  currentFile?: AppointmentFileIdbRow | null;
  fileQueue: AppointmentFileIdbRow[];
  observedFileIds: string[];
  currentFileProgress?: number;
  isOnline: boolean;
};

const getFileToSync = fromPromise<Awaited<ReturnType<GetFileFn>>>(async () => {
  throw new Error('getFilesToSync not implemented');
});

const uploadFile = fromCallback<UploadFileEvents, UploadFileMachineInput>(
  () => {
    throw new Error('uploadFile not implemented');
  },
);

const updateLastAttemptedAt = fromPromise<
  void,
  {
    id: string;
    attemptCount: number;
  }
>(async () => {
  throw new Error('updateLastAttemptedAt not implemented');
});

export type UploadFileCallback = CallbackActorInvoke<
  UploadFileMachineInput,
  UploadFileEvents
>;

export const periodicRemoteFileSyncMachine = setup({
  types: {
    input: {} as Input,
    context: {} as Context,
    events: {} as Events,
    emitted: {} as UploadFileEvents & {
      appointmentId: string;
      fileName: string;
      format: SupportedAudioFormat;
      shouldBeTranscribed: boolean;
      wasDegraded: boolean;
      recordingStartedAt: string;
      attemptNumber: number;
      uploadType?: UploadType;
      chunkNum?: number;
      lastUploadAttemptedAt?: string;
    },
  },
  actors: {
    getFileToSync,
    uploadFile,
    updateLastAttemptedAt,
    lockManagerActor,
  },
  actions: {
    log: logAction,
    completeSyncAttempt: enqueueActions(({ enqueue }) => {
      enqueue.assign({
        iterationCount: ({ context }) => context.iterationCount + 1,
        currentFile: undefined,
        currentFileProgress: undefined,
      });
    }),
    pullNextFileFromQueue: assign(({ context }) => {
      const [nextFile, ...remainingFiles] = context.fileQueue;
      return {
        fileQueue: remainingFiles,
        currentFile: nextFile,
      };
    }),
    trackObservedFileId: assign(({ context }) => {
      const currentFileId = context.currentFile?.id;

      return {
        observedFileIds: currentFileId
          ? [...context.observedFileIds, currentFileId]
          : context.observedFileIds,
      };
    }),
    addFileToQueue: enqueueActions(({ context, event, enqueue }) => {
      assertEvent(event, 'IMMEDIATE_SYNC_AVAILABLE');

      const existingFileIndex = context.fileQueue.findIndex(
        (f) => f.id === event.file.id,
      );

      // File not in queue, add it
      if (existingFileIndex === -1) {
        enqueue.assign({
          fileQueue: [...context.fileQueue, event.file],
        });
        return;
      }

      // File exists - replace only if new attempt count is higher
      const existingFile = context.fileQueue[existingFileIndex];
      if (existingFile && event.file.attemptCount > existingFile.attemptCount) {
        const newQueue = [...context.fileQueue];
        newQueue[existingFileIndex] = event.file;
        enqueue.assign({
          fileQueue: newQueue,
        });
      }
    }),
    resetObservedFileIds: assign({
      observedFileIds: [],
    }),

    updateDeviceOnlineStatus: assign(({ event }) => {
      assertEvent(event, ['DEVICE_ONLINE', 'DEVICE_OFFLINE']);
      return {
        isOnline: event.type === 'DEVICE_ONLINE',
      };
    }),
    emitEvent: enqueueActions(({ enqueue, event, context }) => {
      assertEvent(event, [
        'UPLOAD_FILE.DONE',
        'UPLOAD_FILE.FAILED',
        'UPLOAD_FILE.UPLOADING',
        'UPLOAD_FILE.FILE_UPLOADED',
      ]);

      if (context.currentFile) {
        enqueue.emit({
          ...event,
          appointmentId: context.currentFile.appointmentId,
          fileName: context.currentFile.id,
          format: getSupportedAudioFormat(),
          shouldBeTranscribed: context.currentFile.shouldBeTranscribed ?? true,
          wasDegraded: context.currentFile.wasDegraded ?? false,
          recordingStartedAt: recordingTimestampFromFileName(
            context.currentFile.id,
          ),
          attemptNumber: context.currentFile.attemptCount,
          uploadType: context.currentFile.uploadType as UploadType,
          chunkNum: context.currentFile.chunkNum,
          lastUploadAttemptedAt: context.currentFile.lastUploadAttemptedAt,
        });
      }
    }),
  },
  guards: {
    canAttemptSync: ({ context }) => context.isOnline,
    isRepeatFile: ({ context }) => {
      return context.observedFileIds.includes(context.currentFile?.id ?? '');
    },
    isOffline: ({ context }) => !context.isOnline,
    canSyncWithItems: ({ context }) =>
      Boolean(context.fileQueue.length) &&
      context.isOnline &&
      !context.observedFileIds.includes(context.currentFile?.id ?? ''),
  },
  delays: {
    syncInterval: ({ context }) => context.syncIntervalMs,
  },
}).createMachine({
  /** @xstate-layout N4IgpgJg5mDOIC5QAcwCcCWB7CGDGAYhgDZgDKAngHZ4DEAkgLKMCiAIvQIIAqLA+mQCaAOQDCfTgDVO9ADKcAQrJYBtAAwBdRCiywMAF2xVtIAB6IAtAEY1AJgAcAOgCsAFlvO1ANgCcP184A7ADMtgA0IBSWVrbBao5+wfY+toFeVsleAL5ZEaiYOPhEpJQ0tJwAChUA8vTC3Kz1Atw8AKpkfKIAEpzCAOLs6lpIIMi6BkYm5ggWXvaBjoFWgWpWa3FqdoERUTMxy45zXl6u7im2Xs72OXno2LiEJOTUdGwskvSi-NUEBLJ1qk0JjGekMWGMI2mFmccwSnlcgR8aiRanstisO2iq2ciwczisMJ8K3s7huozuhUeJRejnoVAmAENiBgAF4YKhQUp0IbA8ZgiGgKFInyOVL49zIuaBWzhSKWJELWxqEI2JU+Nz2bK5ckFB7FZ40Wn0wxM1nszkvWgqKzDHSgyaQywnWyOVyrVZS1KuewYuUzFY4nxeJVpBz+YJWLW3XVFJ5co2M5lsjlcq22W2jPkOwWWNyuRbeZzBLwRtxK5yYmbODyOOJWULu6veqxk-L3WPUw10xNmlOWlTBDMgibgqa55GHFbSual4teSsWF3pIvVn2nevV4Ktil6uM00QACzAeAA1uaCFg0PrYLQIOCwI52QA3LAnh9tyn6+OH49njkXq8nlgBBnywPAGX5IYeRGYd+THGY-HzQJPBDH0riJCs-QsddHHrWJglcfwYUuC5txjKkDTwRwf1Pc9L2vW970fKgXzfRwP13TsqJov8oAA68QJYsCIKMKCbV5e1R0dGY0ScRFxULdC1Ew3ZrFhCMPHVZFgj8GUyPbCjvyPWj-3ooDaHQNBL3Y4gIIAM0vABbdidw7SjqOM3j+KAwSX3AyDNGgu0RwFMwsVOUVgiLes0WcGEVMsZCXU1WxXC8ZDjgREJ9M-PdDQAdQZCZTLQYQwFMfRUyYVgOB4fghDECRpDkRRlCCzNJNCqE1lSRYSVOfF8TRaUFzWEVXGLQJklcdZvCDHLOPcwrir4y8yoq1N2tg7Mwr2SMvEcH0Z3RVZkmLBclwJHS1GCaVbD8U6Frc+NlsMEr1sqy1TFgfQIIfBk7P0dAAApYBeOkgbQJ8mQASloDjnppV66NK8rPpoLasyknM9nul19sQsUrB8eZKyWFwbE8YNzmSBwnsMmlOH0IHHOQN6LTKVoKlkapODYPgCDkFhHAIGRlDYTHOvgxdi0OknkXsAiSYcesF0ReIdMIxF0XmUjtQRhnDSZlm2fNVMuZ5vmBaFxw2GqYRASHLGusQGFggSOJCPsZFNNuhdvacGaSW0k4rkCQJ6a-RnmbAVn2fN7nef5wXlEcC2k7qPpJZC+D3asCbpQ2ctg2J7YsPV2t-CJFIMmnSO8qo43Y9NvtOcTq2U+Fzu+HTvnBiBGDnfghx83DNQJs1GwfF9VTw4WVFw7iQJCORK5664xwm7js3GbwABHABXDBMA5WQwJPWgedEABpCRRAARVaegACV+6dqXpKG11J+cEnl4JZCC4kgiiSFNHwBETgzRCM4de7kt4tw5o3feR8T5QDPqeS+1Qb4CzFm-CSOdpLYXHocBwSVpRFmUhdOKjhNjFgwqcRe2V9auUNo3GO29W7cU8ijZ+YBUAQX1FaAewU4KEPRDiNESQ3TolumsZUZMRQk1CNPO6hEAjXGYeRKORt2EIKMr+HhfCwACKeFacSg8P44wsCTQ6GwkSRnzt6asQCfSHDdBGKK913ER00QZbRbCTbxxpBUNAfCGShNaMgYgWAGS4A5IxKgD5QJsQPsgCAf1ZAMh+vAoGEAmbZ1ETjTWNDvSrHVCSfE6VXALiCIdS4MIAjgODOkLcvjcobxyTvQ0ISwkRKiTEuJUALJoCsmgGy9knKOFSekoGmTsm6Nyfk4RHUCE4xsAsKRXojgkgIglGYJwEj2G9tFVKv98StOjH4hujhqh2TssyRJtA3gfC+Hwe2-wHYFJ2kKN0csiSRj8CcG6PhRrYlxPYQavhiSkjJFQHAcBgQsP8fgwpu1rAEXiATfwRM-6jRsO7ZS+cCQpGCKS0lsD4zdhNEmLpeAUXfOiLEKwLgJ42AyMqDc-tgx1I0nMeE0ofGXPae5HiKNrz0uxmimwM1DjgLiiScBDh7ALi8N4FwdhUTTzisTSMFKkZFXZgBD6XIJUuwQkSGhxIgg3U1G6apfoViLHxFNeYJMUirFsHqnRgTaWmulky3CXjbrL28MTWUqljiBxlMvUl4DkLzC9QE5uQSjbIOPuadBJ4-XSWlAkZSdg+W-ySMiFxckZQ+zivhNIibN4LNpR5AxJVeH8P0PqbNViMh5yDeHN06QUg1NRKKGNbpFVBmVK4GtnTOGOB6cgcJYBInRNieadtu187xF8LGmunhw7gKoUuCFKx6y7t8KiSddbp2LoGeeJ4q7piTVrGkNI475KRkrDKpY09i2VPAQSGttz7nsjAHep0uaghFxRNKIMC5hSLGntPAkqR80TpyFkIAA */
  id: 'periodicFileSync',

  context: ({ input: { syncIntervalMs, tenantId, isOnline, userId } }) => ({
    syncIntervalMs: syncIntervalMs ?? SYNC_INTERVAL_MS_DEFAULT,
    iterationCount: 0,
    tenantId,
    fileQueue: [],
    observedFileIds: [],
    isOnline,
    userId,
  }),

  on: {
    IMMEDIATE_SYNC_AVAILABLE: {
      actions: [
        'addFileToQueue',
        {
          type: 'log',
          params: ({ event }) => ({
            level: 'info',
            message: 'Immediate sync requested',
            logMeta: { fileName: event.file.id },
          }),
        },
      ],
    },

    DEVICE_OFFLINE: [
      {
        target: '.Offline',
        actions: ['updateDeviceOnlineStatus'],
      },
    ],
  },
  initial: 'InitializingSync',
  states: {
    InitializingSync: {
      entry: [
        {
          type: 'log',
          params: ({ context }) => ({
            level: 'debug',
            message: 'InitializingSync state',
            logMeta: {
              isOnline: context.isOnline,
              hasItemsInQueue: context.fileQueue.length > 0,
            },
          }),
        },
      ],
      always: [
        {
          guard: 'isOffline',
          target: 'Offline',
        },
        {
          description:
            'If there is already a file in queue, and it has not been attempted, process it next',
          guard: 'canSyncWithItems',
          actions: ['pullNextFileFromQueue'],
          target: 'AttemptingSync',
        },
        {
          target: 'CheckingForFiles',
          guard: 'canAttemptSync',
        },
        {
          target: 'WaitingForNextSync',
        },
      ],
    },

    CheckingForFiles: {
      description: 'Checking to see if there are is a file available to sync.',
      entry: [
        {
          type: 'log',
          params: {
            level: 'debug',
            message: 'Checking for files to sync',
          },
        },
      ],
      invoke: {
        src: 'getFileToSync',
        onDone: [
          {
            description: 'File available - proceed to sync.',
            guard: (ctx) => Boolean(ctx.event.output),
            actions: [
              assign({
                currentFile: (ctx) => ctx.event.output,
              }),
            ],
            target: 'AttemptingSync',
          },
          {
            description: 'If there is no file to sync, enter a waiting state.',
            target: 'WaitingForNextSync',
            actions: 'completeSyncAttempt',
          },
        ],
        onError: [
          {
            target: 'InitializingSync',
          },
        ],
      },
    },

    WaitingForNextSync: {
      description:
        'Waiting for the next sync interval or for an immediate sync to be triggered.',

      entry: [
        'resetObservedFileIds',
        {
          type: 'log',
          params: {
            level: 'debug',
            message:
              'No files available for sync. Will retry at next interval.',
          },
        },
      ],

      after: {
        syncInterval: {
          target: 'InitializingSync',
        },
      },

      on: {
        IMMEDIATE_SYNC_AVAILABLE: {
          target: 'InitializingSync',
          reenter: true,
          actions: 'addFileToQueue',
        },
      },

      always: {
        target: 'InitializingSync',
        guard: 'canSyncWithItems',
      },
    },

    AttemptingSync: {
      initial: 'CheckingForRepeatFile',

      invoke: [
        {
          src: 'lockManagerActor',
          id: 'lockManagerActor',
          input: ({ context }) => ({
            id: context.currentFile?.id,
          }),
        },
      ],

      states: {
        AcquiringLock: {
          description:
            'Attempting to acquire lock for file before proceeding with upload',

          on: {
            LOCK_ACQUIRED: {
              target: 'PrepareUploading',

              actions: [
                {
                  type: 'log',
                  params: ({ event }) => ({
                    level: 'info',
                    message: 'Successfully acquired lock',
                    logMeta: {
                      fileName: event.id,
                    },
                  }),
                },
              ],

              reenter: true,
            },
            LOCK_FAILED: {
              target: '#periodicFileSync.InitializingSync',
              actions: [
                {
                  type: 'log',
                  params: ({ event }) => ({
                    level: 'info',
                    message: 'Failed to acquire lease, file locked',
                    logMeta: {
                      fileName: event.id,
                    },
                  }),
                },
              ],
            },
          },

          entry: 'trackObservedFileId',
        },
        CheckingForRepeatFile: {
          description:
            'Make sure that this is not a repeat of the last file. This could indicate a single stuck file in a loop.',
          always: [
            {
              guard: 'isRepeatFile',
              target: '#periodicFileSync.WaitingForNextSync',
              actions: [
                {
                  type: 'log',
                  params: ({ event }) => ({
                    level: 'info',
                    message: 'File has been observed and marked as repeat',
                    logMeta: {
                      eventType: event.type,
                    },
                  }),
                },
              ],
            },
            {
              target: 'AcquiringLock',
              reenter: true,
            },
          ],
        },

        PrepareUploading: {
          invoke: {
            src: 'updateLastAttemptedAt',
            id: 'updateLastAttemptedAt',
            input: ({ context }) => {
              return {
                id: context.currentFile?.id ?? '',
                attemptCount: (context.currentFile?.attemptCount ?? 0) + 1,
              };
            },
            onDone: {
              target: 'UploadingFile',
            },
            onError: {
              actions: [
                {
                  type: 'log',
                  params: ({ context, event }) => ({
                    level: 'warn',
                    message: formatErrorForLogging(event.error),
                    logMeta: {
                      currentFileId: context.currentFile?.id,
                      attemptCount: context.currentFile?.attemptCount,
                    },
                  }),
                },
              ],
              target: 'UploadingFile',
            },
          },

          description: `Tries to update the last attempted upload time in IndexDB for a given record.`,
        },

        UploadingFile: {
          invoke: {
            src: 'uploadFile',
            input: ({ context: { currentFile, tenantId, userId } }) => {
              if (!currentFile) {
                throw new Error('Invariant: currentFile is not set');
              }

              const {
                id: fileName,
                shouldBeTranscribed,
                ...fileProps
              } = currentFile;

              const isChunked = isAudioFileChunkFile(fileName);

              const uploadType: UploadType =
                fileProps.uploadType ??
                (isChunked ? 'CHUNKED_AUDIO' : 'USER_BACKUP_AUDIO');

              return {
                fileName,
                ...fileProps,
                tenantId,
                userId,
                shouldBeTranscribed: shouldBeTranscribed ?? true,
                uploadType,
                format: getSupportedAudioFormat(),
                recordingStartedAt: recordingTimestampFromFileName(fileName),
              };
            },
          },
        },
      },

      on: {
        'UPLOAD_FILE.FAILED': {
          target: 'InitializingSync',
          actions: [
            {
              type: 'log',
              params: ({ context }) => ({
                level: 'warn',
                message: `File ${context.currentFile?.id} upload failed, will retry later`,
              }),
            },
            'emitEvent',
          ],
        },
        'UPLOAD_FILE.DONE': {
          target: 'InitializingSync',
          actions: ['emitEvent'],
        },
        'UPLOAD_FILE.UPLOADING': {
          actions: [
            assign({
              currentFileProgress: ({ event }) => event.progress,
            }),
            'emitEvent',
          ],
        },
        'UPLOAD_FILE.FILE_UPLOADED': {
          actions: ['emitEvent'],
        },
      },

      exit: 'completeSyncAttempt',
    },

    Offline: {
      on: {
        DEVICE_ONLINE: {
          actions: ['updateDeviceOnlineStatus'],
          target: 'InitializingSync',
        },
      },
    },
  },
});

export const createPeriodicFileMachineLogic = (args: {
  actions?: {
    log: LogAction;
  };
  actors: {
    getFileToSync: GetFileFn;
    uploadFile: UploadFileCallback;
    updateLastAttemptedAt: ({
      id,
      attemptCount,
    }: {
      id: string;
      attemptCount: number;
    }) => Promise<void>;
  };
}) => {
  const {
    actions: { log } = { log: logAction },
    actors: { getFileToSync, uploadFile, updateLastAttemptedAt },
  } = args;

  return periodicRemoteFileSyncMachine.provide({
    actions: {
      log,
    },
    actors: {
      getFileToSync: fromPromise(getFileToSync),
      uploadFile: fromCallback<UploadFileEvents, UploadFileMachineInput>(
        ({ input, sendBack, emit }) => uploadFile({ input, sendBack, emit }),
      ),
      updateLastAttemptedAt: fromPromise(({ input: { attemptCount, id } }) =>
        updateLastAttemptedAt({
          id,
          attemptCount,
        }),
      ),
    },
  });
};

export type PeriodicRemoteFileSyncActor = ActorRefFrom<
  typeof periodicRemoteFileSyncMachine
>;
